<script setup>
import { ref, computed, reactive, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import MessageAPI from 'dashboard/api/inbox/message';

const props = defineProps({
  conversation: { type: Object, required: true },
  onClose: { type: Function, required: true },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const dialogRef = ref(null);

// ── Basic fields ──────────────────────────────────────────────────
const subject = ref('');
const description = ref('');

// ── Pending ticket properties (mirrors TicketDetailView logic) ────
const pendingProps = reactive({
  status: 'open',
  priority: 'no_priority',
  ticket_type: '',
  due_date: '',
  tags: [],
  assignee: null,
  custom_fields: {},
});

const openDropdown = ref(null);
const customFieldSearch = reactive({});
const agentSearch = ref('');
const tagInput = ref('');

// ── Store getters ─────────────────────────────────────────────────
const uiFlags = computed(() => store.getters['tickets/getUIFlags']);
const fieldSettings = computed(() => store.getters['ticketFieldSettings/getTicketFieldSettings'] || []);
const agents = computed(() => store.getters['agents/getAgents'] || []);

const SYSTEM_KEYS = ['status', 'priority', 'ticket_type', 'due_date', 'tags', 'assignee'];

const fieldOptions = key => {
  const f = fieldSettings.value.find(s => s.field_key === key);
  return f?.options || [];
};

// ── Visible fields: system always shown, custom filtered by show_when ──
const visibleFields = computed(() => {
  const cf = pendingProps.custom_fields ?? {};
  return fieldSettings.value
    .filter(f => {
      if (!f.enabled) return false;
      if (SYSTEM_KEYS.includes(f.field_key)) return true;
      const sw = f.conditions?.show_when;
      if (!sw) return true;
      const parentVal = SYSTEM_KEYS.includes(sw.field_key)
        ? pendingProps[sw.field_key]
        : (cf[sw.field_key] ?? null);
      return parentVal === sw.value;
    })
    .sort((a, b) => a.position - b.position);
});

// ── Ticket type L1/L2/L3 cascade ─────────────────────────────────
const selectedTypeL1 = computed(() => {
  const val = pendingProps.ticket_type;
  if (!val) return null;
  return fieldOptions('ticket_type').find(o => o.value === val) ?? null;
});
const selectedTypeL2 = computed(() => {
  const l2Val = pendingProps.custom_fields?.ticket_type_l2;
  if (!l2Val || !selectedTypeL1.value?.children?.length) return null;
  return selectedTypeL1.value.children.find(c => c?.label === l2Val) ?? null;
});
const selectedTypeL3 = computed(() => {
  const l3Val = pendingProps.custom_fields?.ticket_type_l3;
  if (!l3Val || !selectedTypeL2.value?.children?.length) return null;
  return selectedTypeL2.value.children.find(c => c?.label === l3Val) ?? null;
});

// ── Custom dropdown cascade map ───────────────────────────────────
const customCascade = computed(() => {
  const cf = pendingProps.custom_fields ?? {};
  const result = {};
  fieldSettings.value.forEach(f => {
    if (f.field_type !== 'dropdown' || SYSTEM_KEYS.includes(f.field_key)) return;
    const l1Val = cf[f.field_key];
    const l1Opt = l1Val ? (f.options?.find(o => o?.label === l1Val) ?? null) : null;
    const l2Val = cf[f.field_key + '_l2'];
    const l2Opt = (l2Val && l1Opt?.children?.length)
      ? (l1Opt.children.find(c => c?.label === l2Val) ?? null) : null;
    const l3Val = cf[f.field_key + '_l3'];
    const l3Opt = (l3Val && l2Opt?.children?.length)
      ? (l2Opt.children.find(c => c?.label === l3Val) ?? null) : null;
    result[f.field_key] = { l1Opt, l2Opt, l3Opt };
  });
  return result;
});

// ── Filtered agents ───────────────────────────────────────────────
const filteredAgents = computed(() =>
  agents.value.filter(a => !agentSearch.value || a.name.toLowerCase().includes(agentSearch.value.toLowerCase()))
);

// ── Configs & labels ──────────────────────────────────────────────
const priorityConfig = {
  no_priority: { color: 'bg-slate-400' },
  none:        { color: 'bg-slate-400' },
  low:         { color: 'bg-slate-400' },
  medium:      { color: 'bg-blue-500' },
  high:        { color: 'bg-yellow-500' },
  urgent:      { color: 'bg-red-500' },
};

const statusConfig = {
  open:     { class: 'bg-green-100 text-green-800' },
  pending:  { class: 'bg-yellow-100 text-yellow-800' },
  resolved: { class: 'bg-blue-100 text-blue-800' },
  closed:   { class: 'bg-slate-100 text-slate-600' },
};

const typeConfig = {
  question:        { color: 'bg-blue-400' },
  incident:        { color: 'bg-red-400' },
  problem:         { color: 'bg-orange-400' },
  feature_request: { color: 'bg-purple-400' },
  other:           { color: 'bg-slate-400' },
};

const statuses = computed(() => {
  const opts = fieldOptions('status');
  if (opts.length) return opts.map(o => o.value);
  return ['open', 'pending', 'resolved', 'closed'];
});
const statusLabel = computed(() => {
  const opts = fieldOptions('status');
  const map = {};
  if (opts.length) opts.forEach(o => { map[o.value] = o.label; });
  else Object.assign(map, { open: t('TICKETS.STATUS.OPEN'), pending: t('TICKETS.STATUS.PENDING'), resolved: t('TICKETS.STATUS.RESOLVED'), closed: t('TICKETS.STATUS.CLOSED') });
  return map;
});

const priorities = computed(() => {
  const opts = fieldOptions('priority');
  if (opts.length) return opts.map(o => o.value);
  return ['no_priority', 'low', 'medium', 'high', 'urgent'];
});
const priorityLabel = computed(() => {
  const opts = fieldOptions('priority');
  const map = {};
  if (opts.length) opts.forEach(o => { map[o.value] = o.label; });
  else Object.assign(map, { no_priority: t('TICKETS.PRIORITY.NO_PRIORITY'), none: t('TICKETS.PRIORITY.NO_PRIORITY'), low: t('TICKETS.PRIORITY.LOW'), medium: t('TICKETS.PRIORITY.MEDIUM'), high: t('TICKETS.PRIORITY.HIGH'), urgent: t('TICKETS.PRIORITY.URGENT') });
  return map;
});

const ticketTypes = computed(() => {
  const opts = fieldOptions('ticket_type');
  if (opts.length) return opts.map(o => o.value);
  return ['question', 'incident', 'problem', 'feature_request', 'other'];
});
const ticketTypeLabel = computed(() => {
  const opts = fieldOptions('ticket_type');
  const map = {};
  if (opts.length) opts.forEach(o => { map[o.value] = o.label; });
  else Object.assign(map, { question: t('TICKETS.TYPE.QUESTION'), incident: t('TICKETS.TYPE.INCIDENT'), problem: t('TICKETS.TYPE.PROBLEM'), feature_request: t('TICKETS.TYPE.FEATURE_REQUEST'), other: t('TICKETS.TYPE.OTHER') });
  return map;
});

const getInitials = name => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
};

// ── Dropdown helpers ──────────────────────────────────────────────
const toggleDropdown = key => {
  openDropdown.value = openDropdown.value === key ? null : key;
  agentSearch.value = '';
  if (key && customFieldSearch[key] === undefined) customFieldSearch[key] = '';
};

const selectProp = (key, value) => {
  const updated = { ...pendingProps.custom_fields };
  delete updated[key + '_l2'];
  delete updated[key + '_l3'];
  delete updated[key + '_l4'];
  if (key === 'ticket_type') {
    fieldSettings.value.forEach(f => {
      if (SYSTEM_KEYS.includes(f.field_key)) return;
      const sw = f.conditions?.show_when;
      if (sw && sw.field_key === 'ticket_type' && sw.value !== value) {
        delete updated[f.field_key];
        delete updated[f.field_key + '_l2'];
        delete updated[f.field_key + '_l3'];
        delete updated[f.field_key + '_l4'];
      }
    });
  }
  pendingProps[key] = value;
  pendingProps.custom_fields = updated;
  openDropdown.value = null;
};

const updateCustomField = (key, value) => {
  const updated = { ...pendingProps.custom_fields, [key]: value };
  if (key.endsWith('_l3')) {
    const base = key.slice(0, -3);
    delete updated[base + '_l4'];
  } else if (key.endsWith('_l2')) {
    const base = key.slice(0, -3);
    delete updated[base + '_l3'];
    delete updated[base + '_l4'];
  } else if (!key.endsWith('_l4')) {
    delete updated[key + '_l2'];
    delete updated[key + '_l3'];
    delete updated[key + '_l4'];
  }
  pendingProps.custom_fields = updated;
  openDropdown.value = null;
};

const selectAssignee = agent => {
  pendingProps.assignee = agent ? { id: agent.id, name: agent.name } : null;
  openDropdown.value = null;
};

const addTag = () => {
  const tag = tagInput.value.trim().toLowerCase();
  if (!tag || pendingProps.tags.includes(tag)) { tagInput.value = ''; return; }
  pendingProps.tags = [...pendingProps.tags, tag];
  tagInput.value = '';
};
const removeTag = tag => { pendingProps.tags = pendingProps.tags.filter(t => t !== tag); };

// ── Mount ─────────────────────────────────────────────────────────
onMounted(() => {
  dialogRef.value?.open();
  store.dispatch('ticketFieldSettings/fetchAll');
  store.dispatch('agents/get');
  const assignee = props.conversation.meta?.assignee;
  if (assignee?.id) {
    pendingProps.assignee = { id: assignee.id, name: assignee.name };
  }
});

// ── Description file attachments ──────────────────────────────────
const descFiles = ref([]);
const descFileInput = ref(null);
const triggerDescFileInput = () => descFileInput.value?.click();
const onDescFilesSelected = (event) => {
  const files = Array.from(event.target.files || []);
  descFiles.value = [...descFiles.value, ...files];
  event.target.value = '';
};
const removeDescFile = (index) => {
  descFiles.value = descFiles.value.filter((_, i) => i !== index);
};
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ── Submit ────────────────────────────────────────────────────────
const submit = async () => {
  if (!subject.value.trim()) return;
  const payload = {
    subject: subject.value,
    description: description.value,
    contact_id: props.conversation.meta?.sender?.id,
    conversation_id: props.conversation.id,
    status: pendingProps.status || 'open',
  };
  if (pendingProps.priority) payload.priority = pendingProps.priority;
  if (pendingProps.ticket_type) payload.ticket_type = pendingProps.ticket_type;
  if (pendingProps.due_date) payload.due_date = pendingProps.due_date;
  if (pendingProps.tags?.length) payload.tags = pendingProps.tags;
  if (pendingProps.assignee?.id) payload.assignee_id = pendingProps.assignee.id;
  if (Object.keys(pendingProps.custom_fields || {}).length) {
    payload.custom_fields = { ...pendingProps.custom_fields };
  }
  const ticket = await store.dispatch('tickets/create', payload);
  if (descFiles.value.length) {
    await store.dispatch('tickets/uploadDescriptionFiles', { ticketId: ticket.id, files: descFiles.value });
  }
  // Post a private note in the conversation with ticket details
  await MessageAPI.create({
    conversationId: props.conversation.id,
    message: `Ticket created: **${ticket.subject}** (#${ticket.id})\nStatus: ${ticket.status || 'open'}`,
    private: true,
  });
  // Keep conversation open — do NOT resolve
  dialogRef.value?.close();
  props.onClose();
  useAlert(`Ticket #${ticket.id} created successfully`);
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('TICKETS.CONVERT.TITLE')"
    :confirm-button-label="uiFlags.isCreating ? t('TICKETS.CONVERT.CREATING') : t('TICKETS.CONVERT.CONFIRM')"
    :cancel-button-label="t('TICKETS.CANCEL')"
    :disable-confirm-button="!subject.trim() || uiFlags.isCreating"
    :is-loading="uiFlags.isCreating"
    @confirm="submit"
    @close="props.onClose()"
  >
    <div class="space-y-4">
      <!-- Subject -->
      <div>
        <label class="block text-xs font-medium text-slate-500 dark:text-slate-400 uppercase mb-1">
          {{ t('TICKETS.SUBJECT') }} *
        </label>
        <input
          v-model="subject"
          type="text"
          :placeholder="t('TICKETS.SUBJECT_PLACEHOLDER')"
          class="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
        />
      </div>

      <!-- Description -->
      <div>
        <label class="block text-xs font-medium text-slate-500 dark:text-slate-400 uppercase mb-1">
          {{ t('TICKETS.DESCRIPTION') }}
        </label>
        <textarea
          v-model="description"
          rows="3"
          :placeholder="t('TICKETS.DESCRIPTION_PLACEHOLDER')"
          class="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
        />
      </div>

      <!-- Description Attachments -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <label class="block text-xs font-medium text-slate-500 dark:text-slate-400 uppercase mb-1">
            {{ t('TICKETS.ATTACHMENTS') }}
          </label>
          <button
            type="button"
            class="text-xs text-woot-500 hover:text-woot-600"
            @click="triggerDescFileInput"
          >
            + {{ t('TICKETS.ADD_ATTACHMENT') }}
          </button>
          <input
            ref="descFileInput"
            type="file"
            multiple
            class="hidden"
            @change="onDescFilesSelected"
          />
        </div>
        <div v-if="descFiles.length" class="space-y-1">
          <div
            v-for="(file, idx) in descFiles"
            :key="idx"
            class="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800"
          >
            <span class="i-lucide-paperclip size-3.5 text-slate-400 shrink-0" />
            <span class="flex-1 text-xs text-slate-700 dark:text-slate-300 truncate">{{ file.name }}</span>
            <span class="text-xs text-slate-400 shrink-0">{{ formatFileSize(file.size) }}</span>
            <button type="button" class="shrink-0 text-red-400 hover:text-red-600 p-0.5" @click="removeDescFile(idx)">
              <span class="i-lucide-x size-3" />
            </button>
          </div>
        </div>
        <p v-else class="text-xs text-slate-400 italic">{{ t('TICKETS.NO_ATTACHMENTS') }}</p>
      </div>

      <!-- Ticket Properties (mirrors TicketDetailView sidebar) -->
      <div class="border-t border-slate-200 dark:border-slate-700 pt-4 space-y-4">
        <template v-for="field in visibleFields" :key="field.id || field.field_key">

          <!-- Status -->
          <div v-if="field.field_key === 'status'" class="relative">
            <p class="text-xs text-slate-600 dark:text-slate-400 mb-1.5">{{ field.label || t('TICKETS.STATUS_LABEL') }}</p>
            <button
              type="button"
              class="w-full flex items-center justify-between px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700"
              @click="toggleDropdown('status')"
            >
              <span class="px-2 py-0.5 rounded-full text-xs font-medium" :class="statusConfig[pendingProps.status]?.class || 'bg-slate-100 text-slate-600'">
                {{ statusLabel[pendingProps.status] || pendingProps.status }}
              </span>
              <span class="text-slate-400 text-xs">▾</span>
            </button>
            <div v-if="openDropdown === 'status'" class="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden">
              <div class="p-2 border-b border-slate-100 dark:border-slate-700">
                <input v-model="customFieldSearch['status']" type="text" placeholder="Search…" class="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500" />
              </div>
              <div class="max-h-48 overflow-y-auto py-1">
                <template v-for="s in statuses" :key="s">
                  <button
                    v-if="!customFieldSearch['status'] || (statusLabel[s] || s).toLowerCase().includes(customFieldSearch['status'].toLowerCase())"
                    type="button"
                    class="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                    @click="selectProp('status', s)"
                  >
                    <span class="px-2 py-0.5 rounded-full text-xs font-medium" :class="statusConfig[s]?.class || 'bg-slate-100 text-slate-600'">{{ statusLabel[s] || s }}</span>
                    <span v-if="pendingProps.status === s" class="text-woot-500 text-xs">✓</span>
                  </button>
                </template>
              </div>
            </div>
          </div>

          <!-- Priority -->
          <div v-else-if="field.field_key === 'priority'" class="relative">
            <p class="text-xs text-slate-600 dark:text-slate-400 mb-1.5">{{ field.label || t('TICKETS.PRIORITY_LABEL') }}</p>
            <button
              type="button"
              class="w-full flex items-center justify-between px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700"
              @click="toggleDropdown('priority')"
            >
              <span class="flex items-center gap-2">
                <span class="size-2 rounded-full shrink-0" :class="priorityConfig[pendingProps.priority]?.color || 'bg-slate-400'" />
                <span class="text-slate-700 dark:text-slate-300">{{ priorityLabel[pendingProps.priority] || pendingProps.priority }}</span>
              </span>
              <span class="text-slate-400 text-xs">▾</span>
            </button>
            <div v-if="openDropdown === 'priority'" class="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden">
              <div class="p-2 border-b border-slate-100 dark:border-slate-700">
                <input v-model="customFieldSearch['priority']" type="text" placeholder="Search…" class="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500" />
              </div>
              <div class="max-h-48 overflow-y-auto py-1">
                <template v-for="p in priorities" :key="p">
                  <button
                    v-if="!customFieldSearch['priority'] || (priorityLabel[p] || p).toLowerCase().includes(customFieldSearch['priority'].toLowerCase())"
                    type="button"
                    class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                    @click="selectProp('priority', p)"
                  >
                    <span class="size-2 rounded-full shrink-0" :class="priorityConfig[p]?.color || 'bg-slate-400'" />
                    <span class="flex-1 text-left text-slate-700 dark:text-slate-300">{{ priorityLabel[p] || p }}</span>
                    <span v-if="pendingProps.priority === p" class="text-woot-500 text-xs">✓</span>
                  </button>
                </template>
              </div>
            </div>
          </div>

          <!-- Ticket Type + L2/L3/L4 cascade -->
          <div v-else-if="field.field_key === 'ticket_type'">
            <!-- L1 -->
            <div class="relative">
              <p class="text-xs text-slate-600 dark:text-slate-400 mb-1.5">{{ field.label || t('TICKETS.TYPE_LABEL') }}</p>
              <button
                type="button"
                class="w-full flex items-center justify-between px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700"
                @click="toggleDropdown('ticket_type')"
              >
                <span class="flex items-center gap-2">
                  <span class="size-2 rounded-full shrink-0" :class="typeConfig[pendingProps.ticket_type]?.color || 'bg-slate-400'" />
                  <span class="text-slate-700 dark:text-slate-300">{{ ticketTypeLabel[pendingProps.ticket_type] || pendingProps.ticket_type || '— Select —' }}</span>
                </span>
                <span class="text-slate-400 text-xs">▾</span>
              </button>
              <div v-if="openDropdown === 'ticket_type'" class="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden">
                <div class="p-2 border-b border-slate-100 dark:border-slate-700">
                  <input v-model="customFieldSearch['ticket_type']" type="text" placeholder="Search…" class="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500" />
                </div>
                <div class="max-h-48 overflow-y-auto py-1">
                  <template v-for="tp in ticketTypes" :key="tp">
                    <button
                      v-if="!customFieldSearch['ticket_type'] || (ticketTypeLabel[tp] || tp).toLowerCase().includes(customFieldSearch['ticket_type'].toLowerCase())"
                      type="button"
                      class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                      @click="selectProp('ticket_type', tp)"
                    >
                      <span class="size-2 rounded-full shrink-0" :class="typeConfig[tp]?.color || 'bg-slate-400'" />
                      <span class="flex-1 text-left text-slate-700 dark:text-slate-300">{{ ticketTypeLabel[tp] || tp }}</span>
                      <span v-if="pendingProps.ticket_type === tp" class="text-woot-500 text-xs">✓</span>
                    </button>
                  </template>
                </div>
              </div>
            </div>
            <!-- L2 -->
            <div v-if="selectedTypeL1?.children?.length" class="mt-3">
              <p class="text-xs text-slate-600 dark:text-slate-400 mb-1.5">{{ selectedTypeL1.level2_label || selectedTypeL1.sub_label || 'Sub-type' }}</p>
              <div class="relative">
                <button
                  type="button"
                  class="w-full flex items-center justify-between px-3 py-2 text-sm border-l-2 border-l-woot-400 border border-slate-200 dark:border-slate-600 rounded-lg dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700"
                  @click="toggleDropdown('ticket_type_l2')"
                >
                  <span class="text-slate-700 dark:text-slate-300">{{ pendingProps.custom_fields?.ticket_type_l2 || '— Select —' }}</span>
                  <span class="text-slate-400 text-xs">▾</span>
                </button>
                <div v-if="openDropdown === 'ticket_type_l2'" class="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden">
                  <div class="p-2 border-b border-slate-100 dark:border-slate-700">
                    <input v-model="customFieldSearch['ticket_type_l2']" type="text" placeholder="Search…" class="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500" />
                  </div>
                  <div class="max-h-48 overflow-y-auto py-1">
                    <template v-for="sub in selectedTypeL1.children" :key="sub?.value">
                      <button
                        v-if="!customFieldSearch['ticket_type_l2'] || sub.label?.toLowerCase().includes(customFieldSearch['ticket_type_l2'].toLowerCase())"
                        type="button"
                        class="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                        @click="updateCustomField('ticket_type_l2', sub.label)"
                      >
                        <span class="text-slate-700 dark:text-slate-300">{{ sub.label }}</span>
                        <span v-if="pendingProps.custom_fields?.ticket_type_l2 === sub.label" class="text-woot-500 text-xs">✓</span>
                      </button>
                    </template>
                  </div>
                </div>
              </div>
            </div>
            <!-- L3 -->
            <div v-if="selectedTypeL2?.children?.length" class="mt-3">
              <p class="text-xs text-slate-600 dark:text-slate-400 mb-1.5">{{ selectedTypeL2.level3_label || 'Sub-category' }}</p>
              <div class="relative">
                <button
                  type="button"
                  class="w-full flex items-center justify-between px-3 py-2 text-sm border-l-2 border-l-purple-400 border border-slate-200 dark:border-slate-600 rounded-lg dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700"
                  @click="toggleDropdown('ticket_type_l3')"
                >
                  <span class="text-slate-700 dark:text-slate-300">{{ pendingProps.custom_fields?.ticket_type_l3 || '— Select —' }}</span>
                  <span class="text-slate-400 text-xs">▾</span>
                </button>
                <div v-if="openDropdown === 'ticket_type_l3'" class="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden">
                  <div class="p-2 border-b border-slate-100 dark:border-slate-700">
                    <input v-model="customFieldSearch['ticket_type_l3']" type="text" placeholder="Search…" class="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500" />
                  </div>
                  <div class="max-h-48 overflow-y-auto py-1">
                    <template v-for="l3 in selectedTypeL2.children" :key="l3?.value">
                      <button
                        v-if="!customFieldSearch['ticket_type_l3'] || l3.label?.toLowerCase().includes(customFieldSearch['ticket_type_l3'].toLowerCase())"
                        type="button"
                        class="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                        @click="updateCustomField('ticket_type_l3', l3.label)"
                      >
                        <span class="text-slate-700 dark:text-slate-300">{{ l3.label }}</span>
                        <span v-if="pendingProps.custom_fields?.ticket_type_l3 === l3.label" class="text-woot-500 text-xs">✓</span>
                      </button>
                    </template>
                  </div>
                </div>
              </div>
            </div>
            <!-- L4 -->
            <div v-if="selectedTypeL3?.children?.length" class="mt-3">
              <p class="text-xs text-slate-600 dark:text-slate-400 mb-1.5">{{ selectedTypeL3.level4_label || 'Request Name' }}</p>
              <div class="relative">
                <button
                  type="button"
                  class="w-full flex items-center justify-between px-3 py-2 text-sm border-l-2 border-l-pink-400 border border-slate-200 dark:border-slate-600 rounded-lg dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700"
                  @click="toggleDropdown('ticket_type_l4')"
                >
                  <span class="text-slate-700 dark:text-slate-300">{{ pendingProps.custom_fields?.ticket_type_l4 || '— Select —' }}</span>
                  <span class="text-slate-400 text-xs">▾</span>
                </button>
                <div v-if="openDropdown === 'ticket_type_l4'" class="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden">
                  <div class="p-2 border-b border-slate-100 dark:border-slate-700">
                    <input v-model="customFieldSearch['ticket_type_l4']" type="text" placeholder="Search…" class="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500" />
                  </div>
                  <div class="max-h-48 overflow-y-auto py-1">
                    <template v-for="l4 in selectedTypeL3.children" :key="l4?.value">
                      <button
                        v-if="!customFieldSearch['ticket_type_l4'] || l4.label?.toLowerCase().includes(customFieldSearch['ticket_type_l4'].toLowerCase())"
                        type="button"
                        class="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                        @click="updateCustomField('ticket_type_l4', l4.label)"
                      >
                        <span class="text-slate-700 dark:text-slate-300">{{ l4.label }}</span>
                        <span v-if="pendingProps.custom_fields?.ticket_type_l4 === l4.label" class="text-woot-500 text-xs">✓</span>
                      </button>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Due Date -->
          <div v-else-if="field.field_key === 'due_date'">
            <p class="text-xs text-slate-600 dark:text-slate-400 mb-1.5">{{ field.label || t('TICKETS.DUE_DATE') }}</p>
            <input
              type="date"
              :value="pendingProps.due_date"
              class="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
              @change="pendingProps.due_date = $event.target.value"
            />
            <button
              v-if="pendingProps.due_date"
              type="button"
              class="mt-1 text-xs text-slate-400 hover:text-red-500"
              @click="pendingProps.due_date = ''"
            >Clear date</button>
          </div>

          <!-- Tags -->
          <div v-else-if="field.field_key === 'tags'">
            <p class="text-xs text-slate-600 dark:text-slate-400 mb-1.5">{{ field.label || t('TICKETS.TAGS_LABEL') }}</p>
            <div class="flex flex-wrap gap-1.5 mb-2 min-h-[1.5rem]">
              <span
                v-for="tag in pendingProps.tags"
                :key="tag"
                class="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300"
              >
                {{ tag }}
                <button type="button" class="leading-none hover:text-red-500 text-slate-400 ml-0.5" @click="removeTag(tag)">×</button>
              </span>
              <span v-if="!pendingProps.tags.length" class="text-xs text-slate-400 italic">No tags</span>
            </div>
            <div class="flex gap-1">
              <input
                v-model="tagInput"
                type="text"
                :placeholder="t('TICKETS.TAGS_PLACEHOLDER')"
                class="flex-1 min-w-0 px-2 py-1 text-xs border border-slate-300 dark:border-slate-700 rounded dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500"
                @keyup.enter="addTag"
              />
              <button type="button" class="px-2.5 py-1 text-xs bg-woot-500 text-white rounded hover:bg-woot-600 shrink-0" @click="addTag">+</button>
            </div>
          </div>

          <!-- Assignee -->
          <div v-else-if="field.field_key === 'assignee'" class="relative">
            <p class="text-xs text-slate-600 dark:text-slate-400 mb-1.5">{{ field.label || t('TICKETS.ASSIGNEE') }}</p>
            <button
              type="button"
              class="w-full flex items-center justify-between px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700"
              @click="toggleDropdown('assignee')"
            >
              <span class="flex items-center gap-2 min-w-0">
                <span v-if="pendingProps.assignee" class="size-7 rounded-full bg-woot-500 text-white text-xs font-semibold flex items-center justify-center shrink-0">
                  {{ getInitials(pendingProps.assignee.name) }}
                </span>
                <span class="truncate text-slate-700 dark:text-slate-300">{{ pendingProps.assignee?.name || t('TICKETS.UNASSIGNED') }}</span>
              </span>
              <span class="text-slate-400 text-xs shrink-0">▾</span>
            </button>
            <div v-if="openDropdown === 'assignee'" class="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg shadow-lg max-h-56 overflow-hidden flex flex-col">
              <div class="p-2 border-b border-slate-100 dark:border-slate-700">
                <input v-model="agentSearch" type="text" :placeholder="t('TICKETS.AGENT_SEARCH_PLACEHOLDER')" class="w-full px-2 py-1 text-xs border border-slate-200 rounded dark:border-slate-700 dark:bg-slate-700 dark:text-slate-100 focus:outline-none" />
              </div>
              <div class="overflow-y-auto py-1">
                <button type="button" class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700" @click="selectAssignee(null)">
                  <span class="size-7 rounded-full bg-slate-200 dark:bg-slate-600 text-slate-500 text-xs flex items-center justify-center shrink-0">—</span>
                  <span class="flex-1 text-left text-slate-400">{{ t('TICKETS.UNASSIGNED') }}</span>
                  <span v-if="!pendingProps.assignee" class="text-woot-500 text-xs">✓</span>
                </button>
                <button
                  v-for="agent in filteredAgents"
                  :key="agent.id"
                  type="button"
                  class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                  @click="selectAssignee(agent)"
                >
                  <span class="size-7 rounded-full bg-woot-500 text-white text-xs font-semibold flex items-center justify-center shrink-0">{{ getInitials(agent.name) }}</span>
                  <span class="flex-1 text-left text-slate-700 dark:text-slate-300">{{ agent.name }}</span>
                  <span v-if="pendingProps.assignee?.id === agent.id" class="text-woot-500 text-xs">✓</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Custom fields (non-system, already filtered by visibleFields) -->
          <div v-else-if="!SYSTEM_KEYS.includes(field.field_key)">
            <p class="text-xs text-slate-600 dark:text-slate-400 mb-1.5">{{ field.label }}</p>

            <!-- Dropdown: L1 + cascade -->
            <template v-if="field.field_type === 'dropdown'">
              <div class="relative">
                <button
                  type="button"
                  class="w-full flex items-center justify-between px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700"
                  @click="toggleDropdown('custom_' + field.field_key)"
                >
                  <span class="text-slate-700 dark:text-slate-300">{{ pendingProps.custom_fields?.[field.field_key] || '—' }}</span>
                  <span class="text-slate-400 text-xs">▾</span>
                </button>
                <div
                  v-if="openDropdown === 'custom_' + field.field_key"
                  class="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden"
                >
                  <div class="p-2 border-b border-slate-100 dark:border-slate-700">
                    <input
                      :value="customFieldSearch['custom_' + field.field_key] || ''"
                      type="text"
                      placeholder="Search…"
                      class="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500"
                      @input="customFieldSearch['custom_' + field.field_key] = $event.target.value"
                    />
                  </div>
                  <div class="max-h-48 overflow-y-auto py-1">
                    <template v-for="opt in field.options" :key="opt.value">
                      <button
                        v-if="opt != null && (!customFieldSearch['custom_' + field.field_key] || opt.label?.toLowerCase().includes(customFieldSearch['custom_' + field.field_key].toLowerCase()))"
                        type="button"
                        class="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                        @click="updateCustomField(field.field_key, opt.label)"
                      >
                        <span class="text-slate-700 dark:text-slate-300">{{ opt.label }}</span>
                        <span v-if="pendingProps.custom_fields?.[field.field_key] === opt.label" class="text-woot-500 text-xs">✓</span>
                      </button>
                    </template>
                    <p
                      v-if="!field.options?.some(o => o != null && (!customFieldSearch['custom_' + field.field_key] || o.label?.toLowerCase().includes(customFieldSearch['custom_' + field.field_key].toLowerCase())))"
                      class="px-3 py-2 text-xs text-slate-400 text-center"
                    >No options found</p>
                  </div>
                </div>
              </div>
              <!-- L2 -->
              <div v-if="customCascade[field.field_key]?.l1Opt?.children?.length" class="mt-3">
                <p class="text-xs text-slate-600 dark:text-slate-400 mb-1.5">{{ customCascade[field.field_key].l1Opt.level2_label || customCascade[field.field_key].l1Opt.sub_label || (field.label + ' Sub-type') }}</p>
                <div class="relative">
                  <button
                    type="button"
                    class="w-full flex items-center justify-between px-3 py-2 text-sm border-l-2 border-l-woot-400 border border-slate-200 dark:border-slate-600 rounded-lg dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700"
                    @click="toggleDropdown('custom_' + field.field_key + '_l2')"
                  >
                    <span class="text-slate-700 dark:text-slate-300">{{ pendingProps.custom_fields?.[field.field_key + '_l2'] || '— Select —' }}</span>
                    <span class="text-slate-400 text-xs">▾</span>
                  </button>
                  <div
                    v-if="openDropdown === 'custom_' + field.field_key + '_l2'"
                    class="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden"
                  >
                    <div class="p-2 border-b border-slate-100 dark:border-slate-700">
                      <input v-model="customFieldSearch['custom_' + field.field_key + '_l2']" type="text" placeholder="Search…" class="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500" />
                    </div>
                    <div class="max-h-48 overflow-y-auto py-1">
                      <template v-for="sub in customCascade[field.field_key].l1Opt.children" :key="sub?.value">
                        <button
                          v-if="!customFieldSearch['custom_' + field.field_key + '_l2'] || sub.label?.toLowerCase().includes(customFieldSearch['custom_' + field.field_key + '_l2'].toLowerCase())"
                          type="button"
                          class="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                          @click="updateCustomField(field.field_key + '_l2', sub.label)"
                        >
                          <span class="text-slate-700 dark:text-slate-300">{{ sub.label }}</span>
                          <span v-if="pendingProps.custom_fields?.[field.field_key + '_l2'] === sub.label" class="text-woot-500 text-xs">✓</span>
                        </button>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
              <!-- L3 -->
              <div v-if="customCascade[field.field_key]?.l2Opt?.children?.length" class="mt-3">
                <p class="text-xs text-slate-600 dark:text-slate-400 mb-1.5">{{ customCascade[field.field_key].l2Opt.level3_label || 'Sub-category' }}</p>
                <div class="relative">
                  <button
                    type="button"
                    class="w-full flex items-center justify-between px-3 py-2 text-sm border-l-2 border-l-purple-400 border border-slate-200 dark:border-slate-600 rounded-lg dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700"
                    @click="toggleDropdown('custom_' + field.field_key + '_l3')"
                  >
                    <span class="text-slate-700 dark:text-slate-300">{{ pendingProps.custom_fields?.[field.field_key + '_l3'] || '— Select —' }}</span>
                    <span class="text-slate-400 text-xs">▾</span>
                  </button>
                  <div
                    v-if="openDropdown === 'custom_' + field.field_key + '_l3'"
                    class="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden"
                  >
                    <div class="p-2 border-b border-slate-100 dark:border-slate-700">
                      <input v-model="customFieldSearch['custom_' + field.field_key + '_l3']" type="text" placeholder="Search…" class="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500" />
                    </div>
                    <div class="max-h-48 overflow-y-auto py-1">
                      <template v-for="l3 in customCascade[field.field_key].l2Opt.children" :key="l3?.value">
                        <button
                          v-if="!customFieldSearch['custom_' + field.field_key + '_l3'] || l3.label?.toLowerCase().includes(customFieldSearch['custom_' + field.field_key + '_l3'].toLowerCase())"
                          type="button"
                          class="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                          @click="updateCustomField(field.field_key + '_l3', l3.label)"
                        >
                          <span class="text-slate-700 dark:text-slate-300">{{ l3.label }}</span>
                          <span v-if="pendingProps.custom_fields?.[field.field_key + '_l3'] === l3.label" class="text-woot-500 text-xs">✓</span>
                        </button>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
              <!-- L4 -->
              <div v-if="customCascade[field.field_key]?.l3Opt?.children?.length" class="mt-3">
                <p class="text-xs text-slate-600 dark:text-slate-400 mb-1.5">{{ customCascade[field.field_key].l3Opt.level4_label || 'Detail' }}</p>
                <div class="relative">
                  <button
                    type="button"
                    class="w-full flex items-center justify-between px-3 py-2 text-sm border-l-2 border-l-pink-400 border border-slate-200 dark:border-slate-600 rounded-lg dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700"
                    @click="toggleDropdown('custom_' + field.field_key + '_l4')"
                  >
                    <span class="text-slate-700 dark:text-slate-300">{{ pendingProps.custom_fields?.[field.field_key + '_l4'] || '— Select —' }}</span>
                    <span class="text-slate-400 text-xs">▾</span>
                  </button>
                  <div
                    v-if="openDropdown === 'custom_' + field.field_key + '_l4'"
                    class="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden"
                  >
                    <div class="p-2 border-b border-slate-100 dark:border-slate-700">
                      <input v-model="customFieldSearch['custom_' + field.field_key + '_l4']" type="text" placeholder="Search…" class="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded dark:bg-slate-700 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-woot-500" />
                    </div>
                    <div class="max-h-48 overflow-y-auto py-1">
                      <template v-for="l4 in customCascade[field.field_key].l3Opt.children" :key="l4?.value">
                        <button
                          v-if="!customFieldSearch['custom_' + field.field_key + '_l4'] || l4.label?.toLowerCase().includes(customFieldSearch['custom_' + field.field_key + '_l4'].toLowerCase())"
                          type="button"
                          class="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                          @click="updateCustomField(field.field_key + '_l4', l4.label)"
                        >
                          <span class="text-slate-700 dark:text-slate-300">{{ l4.label }}</span>
                          <span v-if="pendingProps.custom_fields?.[field.field_key + '_l4'] === l4.label" class="text-woot-500 text-xs">✓</span>
                        </button>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- Text -->
            <input
              v-else-if="field.field_type === 'text'"
              type="text"
              :value="pendingProps.custom_fields?.[field.field_key] || ''"
              :placeholder="field.label"
              class="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
              @change="updateCustomField(field.field_key, $event.target.value)"
            />

            <!-- Date -->
            <input
              v-else-if="field.field_type === 'date'"
              type="date"
              :value="pendingProps.custom_fields?.[field.field_key] || ''"
              class="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
              @change="updateCustomField(field.field_key, $event.target.value)"
            />

            <!-- Number -->
            <input
              v-else-if="field.field_type === 'number'"
              type="number"
              :value="pendingProps.custom_fields?.[field.field_key] || ''"
              :placeholder="field.label"
              class="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
              @change="updateCustomField(field.field_key, $event.target.value)"
            />

            <!-- Checkbox -->
            <label v-else-if="field.field_type === 'checkbox'" class="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                :checked="!!pendingProps.custom_fields?.[field.field_key]"
                class="size-4 rounded accent-woot-500 cursor-pointer"
                @change="updateCustomField(field.field_key, $event.target.checked)"
              />
              <span class="text-sm text-slate-700 dark:text-slate-300">{{ field.label }}</span>
            </label>
          </div>

        </template>
      </div>
    </div>
  </Dialog>
</template>
